import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import apiRequest, { ApiResponse } from "../axios/useApi";
import { handleApiError } from "../lib/utils";
import { UIContext } from "./UIProvider";

export interface Customer {
  customerId: string; // Unique identifier for the customer
  externalId: string; // External identifier
  firstName: string; // First name of the customer
  lastName: string; // Last name of the customer
  companyName: string; // Name of the company
  address1: string; // Address line 1
  address2: string; // Address line 2
  city: string; // City of the customer
  postalCode: string; // Postal code
  stateId: number; // ID of the state
  countryId: number; // ID of the country
  webSite: string; // Website URL
  email: string; // Email address
  mobileNumber: string; // Mobile number
  phoneNumber: string; // Phone number
  countryCode: string; // Country code
  stateName: string; // Name of the state
  isActive: boolean; // Active status
  createdDateTime: string; // Date and time when the customer was created
  updatedDateTime: string; // Date and time when the customer was last updated
  customerBalance: {
    customerBalanceLogId: string;
    businessMasterId: string;
    customerId: string;
    balanceTypeId: number;
    balanceType: string;
    balanceAmt: number;
    createdDateTime: string;
    updatedDateTime: string;
  };
}

interface CustomerContextType {
  customersData: Customer[];
  isLoading: boolean;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (size: number) => void;
  switchOn: boolean;
  setSwitchOn: (value: boolean) => void;
  applyFilter: () => void;
  searchQuery: string;
  handleSearchClick: () => void;
  handleSearch: (query: string) => void;
  pagination: {
    pageSize: number;
    pageNumber: number;
    totalPages: number;
    totalRecords: number;
  };
}

export const CustomerContext = createContext<CustomerContextType>({
  customersData: [],
  isLoading: false,
  pagination: {
    pageSize: 10,
    pageNumber: 1,
    totalPages: 1,
    totalRecords: 0,
  },
  handlePageChange: () => {},
  handlePageSizeChange: () => {},
  switchOn: false,
  setSwitchOn: () => {},
  applyFilter: () => {},
  searchQuery: "",
  handleSearchClick: () => {},
  handleSearch: () => {},
});

type Props = PropsWithChildren;

export const CustomerProvider = ({ children }: Props) => {
  const { onError, onSuccess, businessId } = useContext(UIContext);
  const [isLoading, setIsLoading] = useState(false);
  const [customersData, setCustomersData] = useState<Customer[]>([]);
  const [switchOn, setSwitchOn] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalPages: 1,
    totalRecords: 0,
  });
  const [searchQuery, setSearchQuery] = useState<string>("");
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const getCustomers = async (
    page: number,
    size: number,
    isActive: boolean,
    term: string
  ) => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        pageSize: size.toString(),
      });

      if (isActive) {
        queryParams.append("isActive", "true");
      }
      if (term) {
        queryParams.append("Name", term);
      }

      const response: ApiResponse<{
        totalPages: number;
        totalRecords: number;
        pageNumber: number;
        pageSize: number;
        items: Customer[];
      }> = await apiRequest({
        url: `/customer/${businessId}/List/${page}?${queryParams.toString()}`,
        method: "GET",
      });

      if (response && response.success) {
        onSuccess?.(
          "GET",
          "getCustomers",
          response?.message || "Data retrieved successfully!"
        );
        const { totalPages, items, pageNumber, totalRecords, pageSize } =
          response.data || {};

        setCustomersData(items || []);
        setPagination({
          pageNumber: pageNumber || 1,
          totalRecords: totalRecords || 10,
          totalPages: totalPages || 1,
          pageSize: pageSize || 10,
        });
      } else {
        handleApiError(response, onError);
      }
    } catch (err) {
      onError?.(new Error("An error occurred while fetching Customers."));
    }
    setIsLoading(false);
  };

  // Fetch customers on page or pageSize change
  useEffect(() => {
    getCustomers(
      pagination.pageNumber,
      pagination.pageSize,
      switchOn,
      searchQuery
    );
  }, []);

  const applyFilter = () => {
    setPagination({ ...pagination, pageNumber: 1 });
    getCustomers(1, pagination.pageSize, switchOn, searchQuery);
  };
  // Handle page size change
  const handlePageSizeChange = (size: number) => {
    setPagination({ ...pagination, pageSize: size, pageNumber: 1 });
    getCustomers(1, size, switchOn, searchQuery);
  };

  // Handle page number change
  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, pageNumber: page });
    getCustomers(page, pagination.pageSize, switchOn, searchQuery);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setIsLoading(true);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    // Debounce API call
    searchTimeout.current = setTimeout(async () => {
      getCustomers(1, pagination.pageSize, switchOn, query);
      setIsLoading(false);
    }, 400);
  };

  const handleSearchClick = () => {
    getCustomers(
      pagination.pageNumber,
      pagination.pageSize,
      switchOn,
      searchQuery
    );
  };

  return (
    <CustomerContext.Provider
      value={{
        isLoading,
        customersData,
        pagination,
        switchOn,
        setSwitchOn,
        handlePageSizeChange,
        handlePageChange,
        applyFilter,
        handleSearchClick,
        handleSearch,
        searchQuery,
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
};
